/**
 * ============================================================
 * src/store/jobs.module.js  –  Job Metadata Vuex Module
 * ------------------------------------------------------------
 * Stores the job types and stages retrieved from the API so
 * every Jobs page shares a single cached copy rather than each
 * page making its own network request.
 *
 * Usage
 * -----
 * In any Jobs page / component:
 *
 *   mounted() {
 *     this.$store.dispatch('jobs/loadMetadata');
 *   }
 *
 * Then read the reactive data via:
 *   this.$store.state.jobs.types       → string[]
 *   this.$store.state.jobs.stages      → { key, label }[]
 *   this.$store.state.jobs.stageKeys   → string[]
 *   this.$store.state.jobs.snakeToTitle → { snake_case: 'Title Case' }
 *
 * If the API call fails the module falls back to the static
 * constants so the UI still works.
 * ============================================================
 */

import { fetchMetadata } from '@/services/jobs.service';
import {
  JOB_STAGES,
  JOB_STAGE_KEYS,
  JOB_STAGE_SNAKE_TO_TITLE,
  JOB_TYPES
} from '@/constants/jobs';

/** Convert a Title-Case stage string to snake_case for the mapping. */
function toSnakeCase(s) {
  return s.toLowerCase().replace(/\s+/g, '_');
}

export const jobs = {
  namespaced: true,

  state: {
    /** e.g. ['New Install', 'Maintenance'] */
    types: [],

    /** e.g. [{ key: 'Created', label: 'Created' }, ...] */
    stages: [],

    /** e.g. ['Created', 'In Preparation', ...] */
    stageKeys: [],

    /** e.g. { in_preparation: 'In Preparation', ... } */
    snakeToTitle: {},

    /** True once the API response has been successfully stored */
    loaded: false,

    /** True while an in-flight request is being awaited */
    loading: false
  },

  mutations: {
    /**
     * setMetadata
     * -----------
     * Stores types and stages returned from GET /jobs/metadata.
     * Derives the stageKeys flat array and the snake_case→Title-Case
     * mapping automatically so callers do not have to.
     */
    setMetadata(state, { types, stages }) {
      state.types       = Array.isArray(types)  ? types  : JOB_TYPES;
      const receivedStages = Array.isArray(stages) ? stages : JOB_STAGE_KEYS;
      state.stages      = receivedStages.map(s => ({ key: s, label: s }));
      state.stageKeys   = receivedStages;
      state.snakeToTitle = Object.fromEntries(receivedStages.map(s => [toSnakeCase(s), s]));
      state.loaded      = true;
      state.loading     = false;
    },

    setLoading(state, val) {
      state.loading = val;
    },

    /**
     * setFallback
     * -----------
     * Called when the API request fails.  Populates the module with
     * the static constants so the UI stays functional.
     */
    setFallback(state) {
      state.types       = JOB_TYPES;
      state.stages      = JOB_STAGES;
      state.stageKeys   = JOB_STAGE_KEYS;
      state.snakeToTitle = JOB_STAGE_SNAKE_TO_TITLE;
      state.loaded      = true;
      state.loading     = false;
    }
  },

  actions: {
    /**
     * loadMetadata
     * ------------
     * Fetches job types and stages from the API exactly once per
     * session (subsequent dispatches are no-ops).  If the request
     * fails it falls back to the hardcoded constants.
     */
    loadMetadata({ state, commit }) {
      if (state.loaded || state.loading) return Promise.resolve();
      commit('setLoading', true);
      return fetchMetadata()
        .then(res => {
          commit('setMetadata', {
            types:  res.data.types,
            stages: res.data.stages
          });
        })
        .catch(() => {
          commit('setFallback');
        });
    }
  }
};
